import { useEffect, useState, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'

interface UseUserLocationProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  /** Property coordinates to fly to instead of user location (for analysis maps) */
  propertyCoordinates?: [number, number]
}

/**
 * Hook to handle user location marker and flyTo functionality
 * If propertyCoordinates are provided, flies to property instead of user's physical location
 */
export const useUserLocation = ({ map, mapLoaded, propertyCoordinates }: UseUserLocationProps) => {
  const [userLocationMarker, setUserLocationMarker] = useState<mapboxgl.Marker | null>(null)

  // Remove user location marker when map moves
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const handleMapMove = () => {
      if (userLocationMarker) {
        userLocationMarker.remove()
        setUserLocationMarker(null)
      }
    }

    // Add event listeners for map interactions that should remove the marker
    map.current.on('dragstart', handleMapMove)
    map.current.on('zoomstart', handleMapMove)
    map.current.on('rotatestart', handleMapMove)
    map.current.on('pitchstart', handleMapMove)

    return () => {
      if (map.current) {
        map.current.off('dragstart', handleMapMove)
        map.current.off('zoomstart', handleMapMove)
        map.current.off('rotatestart', handleMapMove)
        map.current.off('pitchstart', handleMapMove)
      }
    }
  }, [mapLoaded, userLocationMarker])

  // Handle fly to location (property or user)
  const handleFlyToUserLocation = useCallback(() => {
    if (!map.current) return

    // If property coordinates are provided, fly to property instead of user location
    if (propertyCoordinates && propertyCoordinates.length === 2) {
      map.current.flyTo({
        center: propertyCoordinates,
        zoom: 18,
        essential: true,
      })
      return
    }

    // Otherwise, use geolocation to fly to user's physical location
    if (!navigator.geolocation) {
      console.error('Geolocation is not supported by your browser')
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords

        // Fly to user location
        map.current?.flyTo({
          center: [longitude, latitude],
          zoom: 15,
          essential: true,
        })

        // Remove existing marker if any
        if (userLocationMarker) {
          userLocationMarker.remove()
        }

        // Create a new marker at user location
        const marker = new mapboxgl.Marker({
          color: '#0294df',
        })
          .setLngLat([longitude, latitude])
          .addTo(map.current!)

        setUserLocationMarker(marker)
      },
      (error) => {
        console.error('Error getting location:', error)
        let errorMessage = 'Unable to retrieve your location'

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              'Location permission denied. Please enable location access in your browser settings.'
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.'
            break
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.'
            break
        }

        // Show error to user (you might want to implement a toast notification here)
        console.error(errorMessage)
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      }
    )
  }, [map, userLocationMarker, propertyCoordinates])

  return { handleFlyToUserLocation }
}
